import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Video, Clock, Check, X, ExternalLink,
  CalendarCheck, CalendarClock, CalendarX, Link2
} from 'lucide-react';
import { toLocalYMD, formatLocalDate, parseLocalDate } from '../../../lib/dateHelpers';

const AppointmentsSection = ({
  appointments,
  pendingAppointments,
  confirmedAppointments,
  filteredAppointments,
  appointmentFilter,
  setAppointmentFilter,
  handleApproveAppointment,
  handleRejectAppointment,
  handleOpenSuggestModal,
  handleBookForUser,
  showSuggestModal,
  setShowSuggestModal,
  selectedAppointment,
  suggestedDates,
  setSuggestedDates,
  handleSubmitSuggestedDates
}) => {
  const navigate = useNavigate();

  return (
    <>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Appointments</h1>
            <p className="text-sm lg:text-base text-gray-500">Manage mentorship session requests</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button 
              onClick={handleBookForUser}
              className="flex-1 sm:flex-none bg-purple-600 text-white px-3 lg:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm lg:text-base"
            >
              <CalendarCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Book for</span> Mentee
            </button>
            <button 
              onClick={() => navigate('/appointments')}
              className="flex-1 sm:flex-none border px-3 lg:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm lg:text-base bg-white"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">Full</span> Calendar
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          <div className="bg-white rounded-xl border p-3 lg:p-4">
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CalendarClock className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold">{pendingAppointments.length}</p>
                <p className="text-xs lg:text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-3 lg:p-4">
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CalendarCheck className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold">{confirmedAppointments.length}</p>
                <p className="text-xs lg:text-sm text-gray-500">Confirmed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-3 lg:p-4">
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold">{appointments.length}</p>
                <p className="text-xs lg:text-sm text-gray-500">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-3 lg:p-4">
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Video className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold">
                  {appointments.filter(a => a.status === 'confirmed' && parseLocalDate(a.date) >= new Date(new Date().toDateString())).length}
                </p>
                <p className="text-xs lg:text-sm text-gray-500">Upcoming</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          <button 
            onClick={() => setAppointmentFilter('pending')}
            className={`px-3 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap ${
              appointmentFilter === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Pending ({pendingAppointments.length})
          </button>
          <button 
            onClick={() => setAppointmentFilter('confirmed')}
            className={`px-3 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap ${
              appointmentFilter === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Confirmed ({confirmedAppointments.length})
          </button>
          <button 
            onClick={() => setAppointmentFilter('all')}
            className={`px-3 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap ${
              appointmentFilter === 'all' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            All ({appointments.length})
          </button>
        </div>

        {/* Appointments List */}
        <div className="bg-white rounded-xl border">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold">
              {appointmentFilter === 'pending' ? 'Pending Requests' : 
               appointmentFilter === 'confirmed' ? 'Confirmed Sessions' : 'All Appointments'}
            </h2>
          </div>
          <div className="divide-y">
            {filteredAppointments.map((apt) => (
              <div key={apt.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      apt.status === 'pending' ? 'bg-yellow-100' :
                      apt.status === 'confirmed' ? 'bg-green-100' :
                      apt.status === 'cancelled' ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      <Calendar className={`w-6 h-6 ${
                        apt.status === 'pending' ? 'text-yellow-600' :
                        apt.status === 'confirmed' ? 'text-green-600' :
                        apt.status === 'cancelled' ? 'text-red-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{apt.user_name || apt.user_email || 'Unknown Mentee'}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {apt.date ? formatLocalDate(apt.date, { weekday: 'short', day: 'numeric', month: 'short' }) : 'No date'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {apt.time || 'No time'}
                        </span>
                        {apt.session_type && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                            {apt.session_type}
                          </span>
                        )}
                      </div>
                      {apt.notes && (
                        <p className="text-sm text-gray-500 mt-2 italic">"{apt.notes}"</p>
                      )}
                      {apt.zoom_link && (
                        <a 
                          href={apt.zoom_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-2 flex items-center gap-1"
                        >
                          <Link2 className="w-4 h-4" />
                          Join Zoom Meeting
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                      apt.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      apt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {apt.status?.charAt(0).toUpperCase() + apt.status?.slice(1)}
                    </span>
                    
                    {apt.status === 'pending' && (
                      <div className="flex gap-2 ml-4">
                        <button 
                          onClick={() => handleApproveAppointment(apt.id)}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 flex items-center gap-1"
                          title="Approve and create Zoom link"
                        >
                          <Check className="w-4 h-4" /> Approve
                        </button>
                        <button 
                          onClick={() => handleOpenSuggestModal(apt)}
                          className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 flex items-center gap-1"
                          title="Suggest alternative dates"
                        >
                          <CalendarClock className="w-4 h-4" /> Suggest Dates
                        </button>
                        <button 
                          onClick={() => handleRejectAppointment(apt.id)}
                          className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 flex items-center gap-1"
                          title="Reject appointment"
                        >
                          <X className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {filteredAppointments.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No {appointmentFilter} appointments found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Suggest Dates Modal */}
      {showSuggestModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={(e) => { if (e.target === e.currentTarget) setShowSuggestModal(false); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 relative" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Suggest Alternative Dates</h2>
            <p className="text-gray-600 mb-4">
              Suggest up to 3 alternative times for {selectedAppointment.user_name || 'the mentee'}
            </p>
            
            <div className="space-y-4">
              {suggestedDates.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-sm text-gray-500">Date {index + 1}</label>
                    <input 
                      type="date" 
                      className="w-full border rounded-lg px-3 py-2 mt-1"
                      value={item.date}
                      min={toLocalYMD(new Date())}
                      onChange={(e) => {
                        const newDates = [...suggestedDates];
                        newDates[index].date = e.target.value;
                        setSuggestedDates(newDates);
                      }}
                    />
                  </div>
                  <div className="w-32">
                    <label className="text-sm text-gray-500">Time</label>
                    <input 
                      type="time" 
                      className="w-full border rounded-lg px-3 py-2 mt-1"
                      value={item.time}
                      onChange={(e) => {
                        const newDates = [...suggestedDates];
                        newDates[index].time = e.target.value;
                        setSuggestedDates(newDates);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button 
                type="button"
                onClick={() => setShowSuggestModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={() => {
                  console.log('Send Suggestions clicked');
                  handleSubmitSuggestedDates();
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer"
                data-testid="send-suggestions-btn"
              >
                Send Suggestions
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppointmentsSection;
